<template>
    <div class="q_list" @mouseup.stop="changeActiveEditor" @focusin.stop="changeActiveEditorFromFocus">
        <div class="q_txt_area">
            <div class="q_txt">
                <strong>{{questionText}}</strong>
            </div>
            <p class="q_sub_txt" v-if="question.maxChoice">
                <span>{{$t('EnrolmentQuestionViewer_maxChoseProgram', {max: question.maxChoice})}}</span>
            </p>
        </div>
        <div class="bx_include_file" v-if="showImage">
            <div class="image">
                <img :src="question.image.url" alt="" style="max-width: 100%">
            </div>
        </div>
        <div class="a_txt">
            <div
                v-for="program in programs"
                :key="`program-${program.programNo}`"
                class="box_class"
            >
                <strong>
                    {{program.programName}}
                    <template v-if="isShowLimit">
                        <span
                            class="num">{{$t('EnrolmentQuestionViewer_limitCountPerson', {number: program.limit || 0})}}</span>
                        <span
                            class="num">{{$t('EnrolmentQuestionViewer_reservedLimitCountPerson', {number: program.reservedLimit || 0})}}</span>
                    </template>
                </strong>
                <span class="desc_view_txt">
                  {{program.programDesc}}
                </span>
                <ul class="list_class">
                    <li>
                        <strong class="tit">{{$t('EnrolmentQuestionViewer_time')}}</strong>
                        <div class="cont">
                            <span v-if="program.times.length < 1">없음</span>
                            <span
                                v-for="(time, index) in program.times"
                                :key="`program-${program.programNo}-time-${index}`"
                                class="time"
                            >
                                {{makeTimeText({time})}}
                            </span>
                        </div>
                    </li>
                    <li>
                        <strong class="tit">{{$t('EnrolmentQuestionViewer_target')}}</strong>
                        <div class="cont">
                            {{makeTargetText({filters: program.filters})}}
                        </div>
                    </li>
                    <li>
                        <strong class="tit">{{$t('EnrolmentQuestionViewer_cost')}}</strong>
                        <div class="cont">
                            {{$t('EnrolmentQuestionViewer_costUnit', {number: getTotalCost({costs: program.costs})})}}
                            <span
                                v-for="(cost, index) in program.costs"
                                :key="`program-${program.programNo}-cost-${index}`"
                                class="cost"
                            >
                                {{cost.name}} {{$t('EnrolmentQuestionViewer_costUnit', {number: getCommaNumber({number: cost.value}) || 0})}}
                            </span>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
        <button type="button" class="btn_edit">{{$t('EnrolmentQuestionViewer_edit')}}</button>
    </div>
</template>

<script>
import {ENROLMENT_TYPE} from "@/common/consts/survey.js";
import {mapGetters, mapState} from "vuex";
import {GET_FILTER_LABEL} from "@common/store/modules/enrolment/editor.js";
import {REQUEST_STATE} from "@iamteacherpc/consts/request.js";
import whatInput from 'what-input';

export default {
    name: "EnrolmentQuestionViewer",

    props: {
        questionIndex: {
            type: Number,
            required: true,
        },
    },

    computed: {
        ...mapGetters('enrolment', {
            getFilterLabel: GET_FILTER_LABEL,
        }),

        ...mapState('enrolment', {
            filterOptionsFilters: state => state.options.filterOptions.filters,
        }),

        question() {
            return this.$store.state.enrolment.form.questions[this.questionIndex];
        },

        showImage() {
            return this.question.image && this.question.image.uploadState === REQUEST_STATE.SUCCESS;
        },

        questionText() {
                return this.question.question || this.$t('EnrolmentQuestionViewer_inputQuestion');
            },

            programs() {
                return this.question.programs.map((program, index) => ({
                    ...program,
                    programName: program.programName || this.$t('EnrolmentQuestionViewer_inputProgramName'),
                }));
            },

            isShowLimit() {
                const enrolmentType = this.$store.state.enrolment.form.enrolmentType;
                return enrolmentType === ENROLMENT_TYPE.FCFS || enrolmentType === ENROLMENT_TYPE.DRAW_PRIOR;
            }
        },

        methods: {
            changeActiveEditorFromFocus() {
                if (whatInput.ask() !== 'keyboard') {
                    return;
                }

                this.$emit('changeActiveEditor', this.questionIndex);
            },

            changeActiveEditor() {
                this.$emit('changeActiveEditor', this.questionIndex);
            },

            makeTimeText({time}) {
                if(!time.day|| !time.endHour || !time.endMinute || !time.startHour || !time.startMinute) {
                  return '미설정'
                }
                const dayIndex = time.day ?? 7;
                const day = this.$t('EnrolmentQuestionViewer_timeDayNames').split(',')[dayIndex];
                const startHour = this.getPadStartString({string: time.startHour});
                const startMinute = this.getPadStartString({string: time.startMinute});
                const endHour = this.getPadStartString({string: time.endHour});
                const endMinute = this.getPadStartString({string: time.endMinute});

                return `${day} ${startHour}:${startMinute}~${endHour}:${endMinute}`;
            },

            getPadStartString({string}) {
                return (string || '00').padStart(2, '0');
            },

            makeTargetText({filters}) {
                if (!filters.length) {
                    return this.$t('EnrolmentQuestionViewer_selectTarget');
                }

                const text = [];
                let group = [];

                // filters에 값은 정렬되어 있지 않기 때문에 filterOptionsFilters를 기준으로 한다.
                this.filterOptionsFilters.forEach(filterOptionsFilter => {
                    const isIncludes = filters.includes(filterOptionsFilter.value);

                    if (isIncludes) {
                        group.push(filterOptionsFilter.label);
                    } else if (group.length) {
                        group.splice(1, group.length - 2);
                        text.push(group.join('~'));
                        group = [];
                    }
                });

                if (group.length) {
                    group.splice(1, group.length - 2);
                    text.push(group.join('~'));
                }

                return text.join(', ');
            },

            getTotalCost({costs}) {
                const sum = costs.reduce((sum, cost) => sum + (cost.value || 0), 0);
                return this.getCommaNumber({number: sum});
            },

            getCommaNumber({number}) {
                return String(number || 0).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
            },
        }
    }
</script>

<style scoped lang="scss">
.desc_view_txt {
  margin-top:4px;
  color: #666;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: -0.5px;
}
</style>
